import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';

import Header from '../../components/Header';
import PodcastEpisodeCard from '../../components/PodcastEpisodeCard';
import Sidebar from '../../components/Sidebar';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import Hero from '../../components/Hero';

import { generateWebPageSchema } from '../../utils/generateWebPageSchema';

export async function getStaticProps() {
  const fs = require('fs');
  const path = require('path');

  const podcastDir = path.join(process.cwd(), 'pages/podcast');
  const files = fs.readdirSync(podcastDir);

  const episodes = await Promise.all(
    files
      .filter((file) => file.endsWith('.js') && file !== 'index.js')
      .map(async (file) => {
        try {
          const { metadata } = await import(`./${file}`);

          if (!metadata || !metadata.title || !metadata.excerpt) return null;

          const slug = file.replace('.js', '');

          return {
            ...metadata,
            link: `/podcast/${slug}`,
          };

        } catch (err) {
          console.warn(`Failed to load podcast file: ${file}`, err);
          return null;
        }
      })
  );

  const validEpisodes = episodes
    .filter(Boolean)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const categories = Array.from(
    new Set(validEpisodes.map((ep) => ep.category).filter(Boolean))
  );

  const tags = Array.from(
    new Set(validEpisodes.flatMap((ep) => ep.tags || []).filter(Boolean))
  );

  return {
    props: {
      episodes: validEpisodes,
      categories,
      tags,
    },
  };
}

export default function PodcastPage({ episodes, categories, tags }) {
  const router = useRouter();
  const { q } = router.query;

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTag, setActiveTag] = useState(null);

  useEffect(() => {
    if (q) setSearch(q);
  }, [q]);

  const filteredEpisodes = episodes.filter((ep) => {
    const matchesSearch =
      (ep.title || '').toLowerCase().includes(search.toLowerCase()) ||
      (ep.excerpt || '').toLowerCase().includes(search.toLowerCase());

    const matchesCategory = activeCategory ? ep.category === activeCategory : true;
    const matchesTag = activeTag ? ep.tags?.includes(activeTag) : true;

    return matchesSearch && matchesCategory && matchesTag;
  });

  const recentEpisodes = episodes.slice(0, 3);

  return (
    <>
      <Head>
        <title>Modern Build Podcast | Behind the Build</title>

        <meta
          name="description"
          content="Watch real conversations with contractors, homeowners, and remodeling experts on Modern Build: Behind the Build."
        />

        <link
          rel="canonical"
          href="https://modernbuildremodel.com/podcast"
        />


      {/* Open Graph */}
      <meta property="og:title" content="Modern Build Podcast | Behind the Build" />
      <meta property="og:description" content="Modern Build Podcast where you can enjoy and listin to real stories, real contractors, real projects" />
      <meta property="og:image" content="https://modernbuildremodel.com/hero/los-angeles-california-best-home-remodling-contractor.webp" />
      <meta property="og:url" content="https://modernbuildremodel.com/podcast" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Modern Build Home Remodeling" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Modern Build Podcast | Behind the Build" />
      <meta name="twitter:description" content="Modern Build Podcast where you can enjoy and listin to real stories, real contractors, real projects" />
      <meta name="twitter:image" content="https://modernbuildremodel.com/hero/los-angeles-california-best-home-remodling-contractor.webp" />
      <meta name="twitter:site" content="@modernbuildhome" />

        {/* Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Modern Build Podcast',
                description: 'Behind the Build podcast by Modern Build',
                slug: '/podcast',
              })
            ),
          }}
        />
      </Head>

      <Header />

      <Hero category="podcast" page="page" buttonLink="https://www.youtube.com/@modernbuildhomeremodeling" hasContentH1={false}/>

      <section className="bg-white py-16">
        <div className="container mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-12">

          {/* Main */}
          <div className="md:col-span-2 space-y-8">

            {filteredEpisodes.length ? (
              filteredEpisodes.map((ep, index) => (
                <PodcastEpisodeCard key={index} episode={ep} />
              ))
            ) : (
              <p>No episodes match your search.</p>
            )}

          </div>

          {/* Sidebar */}
          <Sidebar
            search={search}
            setSearch={setSearch}
            categories={categories}
            tags={tags}
            recentPosts={recentEpisodes}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            activeTag={activeTag}
            setActiveTag={setActiveTag}
            isPodcast={true}
          />

        </div>
      </section>

      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
